import {
  styled,
  Box,
  Stack,
  Card,
  Tab,
  TabsNavigation,
  Text,
  Button,
  type Theme,
  alpha,
} from '@ltvco/refresh-lib/theme';

export const HelpfulTipsWrapper = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    paddingLeft: 0,
    paddingRight: 0,
  },
}));

export const ResultsHeaderWrapper = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(3),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    paddingLeft: 0,
    paddingRight: 0,
  },
}));

export const FiltersBarWrapper = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

export const LoadingWrapper = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
}));

export const FaqWrapper = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2.5),
  [theme.breakpoints.up('sm')]: {
    marginTop: theme.spacing(4),
  },
}));

export const RelativeContainer = styled(Box)({
  position: 'relative',
});

export const ResultsContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

export const ResultCardsContainer = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
}));

export const NoResultsContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(6),
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  textAlign: 'center',
  maxWidth: 600,
  margin: '0 auto',
}));

export const NoResultsIcon = styled(Box)(({ theme }) => ({
  fontSize: '5rem',
  color: theme.palette.text.secondary,
  opacity: 0.7,
}));

export const NoResultsTipsContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 450,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(3),
  backgroundColor: theme.palette.grey[50],
  border: '1px solid',
  borderColor: theme.palette.grey[300],
  boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.1)}`,
}));

export const NoResultsBullet = styled(Stack)(({ theme }) => ({
  width: '8px',
  color: theme.palette.primary.main,
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: theme.typography.body2.fontSize,
  lineHeight: 1,
}));

export const ResultCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  border: '1px solid',
  borderColor: theme.palette.grey[300],
  borderRadius: theme.spacing(1),
  boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.1)}`,
  transition: 'all 0.2s ease-in-out',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
}));

export const InteractiveResultCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  border: '1px solid',
  borderColor: theme.palette.grey[300],
  borderRadius: theme.spacing(1),
  boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.1)}`,
  transition: 'all 0.2s ease-in-out',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  cursor: 'pointer',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    boxShadow: theme.shadows[4],
    transform: 'translateY(-2px)',
  },
}));

export const PaginationOverlayContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: `${theme.palette.common.white}D9`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10,
  backdropFilter: 'blur(2px)',
}));

export const paginationOverlayCardStyles = {
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  gap: 2,
  backgroundColor: 'common.white',
  borderRadius: 2,
  p: 3,
  boxShadow: 4,
};

export const centerContainerStyles = {
  py: 4,
  textAlign: 'center',
} as const;

export const loadingContainerStyles = {
  mt: 3,
} as const;

export const responsivePaddingStyles = {
  px: { xs: 2, sm: 0 },
} as const;

export const gridResultsContainerStyles = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
  gap: 2,
} as const;

// ============= Reusable Base Components =============

interface IconCircleProps {
  size?: number;
  bgOpacity?: number;
  iconColor?: 'primary' | 'secondary';
}

/**
 * Reusable circular icon container with background
 * Used by: DetailIconContainer, PartyIconContainer, etc.
 */
export const IconCircle = styled(Box, {
  shouldForwardProp: (prop) =>
    !['size', 'bgOpacity', 'iconColor'].includes(prop as string),
})<IconCircleProps>(
  ({ theme, size = 24, bgOpacity = 0.1, iconColor = 'primary' }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: size,
    height: size,
    borderRadius: '50%',
    backgroundColor: alpha(theme.palette.primary.main, bgOpacity),
    flexShrink: 0,
    '& svg': {
      fontSize: size * 0.6,
      color:
        iconColor === 'primary'
          ? theme.palette.primary.main
          : theme.palette.text.secondary,
    },
  })
);

/**
 * Base styles for simple icon wrapper (no background)
 * Used by: LastUpdatedIcon, SortIcon, BuyOnPacerIcon
 */
export const IconWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size?: number }>(({ theme, size = 16 }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: size,
  height: size,
  color: theme.palette.text.secondary,
  '& svg': {
    fontSize: size,
  },
}));

// ============= Base Typography Styles =============

/**
 * Base table cell text styles - reusable across table components
 */
export const getBaseTableCellStyles = (theme: Theme) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '16px',
});

/**
 * Primary text cell styles (dark text)
 */
export const getTableCellPrimaryStyles = (theme: Theme) => ({
  ...getBaseTableCellStyles(theme),
  color: theme.palette.text.primary,
});

/**
 * Secondary text cell styles (lighter text)
 */
export const getTableCellSecondaryStyles = (theme: Theme) => ({
  ...getBaseTableCellStyles(theme),
  color: theme.palette.text.secondary,
});

// ============= Button Styles =============

const getBaseButtonStyles = (theme: Theme) => ({
  textTransform: 'none' as const,
  fontWeight: 500,
  borderRadius: '4px',
  boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.1)}`,
  '&:focus': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.1)}`,
    outline: 'none',
  },
});

const getBaseSmallButtonStyles = (theme: Theme) => ({
  ...getBaseButtonStyles(theme),
  fontSize: '0.75rem',
  padding: '4px 12px',
  height: '28px',
  minWidth: 'auto',
});

export const getDownloadButtonGroupStyles = (theme: Theme) => ({
  display: 'flex',
  alignItems: 'center',
  borderRadius: '4px',
  overflow: 'hidden',
  boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.1)}`,
  '& > button:first-of-type': {
    borderRadius: '4px 0 0 4px',
    borderRight: 'none',
  },
  '& > button:last-of-type': {
    borderRadius: '0 4px 4px 0',
    borderLeft: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
  },
  '@media (max-width: 600px)': {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: 'auto',
    marginTop: '2px',
  },
});

export const getDownloadButtonStyles = (theme: Theme) => ({
  ...getBaseSmallButtonStyles(theme),
  borderRadius: 0,
  boxShadow: 'none',
  px: 1.5,
  '@media (max-width: 600px)': {
    height: '26px',
    fontSize: '0.7rem',
    px: 0.75,
    minWidth: '26px',
    '& .MuiButton-startIcon': {
      margin: 0,
    },
    '& .button-text': {
      display: 'none',
    },
  },
});

export const getDropdownButtonStyles = (theme: Theme) => ({
  ...getBaseSmallButtonStyles(theme),
  minWidth: '28px',
  px: 0.5,
  boxShadow: 'none',
  borderRadius: 0,
  mr: 0.25,
  '@media (max-width: 600px)': {
    height: '26px',
    width: '26px',
    minWidth: '26px',
  },
});

// ============= Shared State Components =============

export const LoadingStateContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  justifyContent: 'center',
}));

export const ErrorStateContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: 'center',
}));

export const EmptyStateContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4, 0),
  textAlign: 'center',
}));

export const DocketEntriesWrapper = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  position: 'relative',
}));

export const DescriptionContentBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  width: '100%',
}));

export const PartiesSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

export const SectionCard = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
  },
}));

// ============= Tab Navigation Styles =============

/**
 * Shared tab navigation styles for CaseDetails and OpinionDetails
 */
export const StyledTabsNavigation = styled(TabsNavigation)(({ theme }) => ({
  marginTop: theme.spacing(3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.primary.main,
    height: '2px',
  },
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(2),
    '& .MuiTabs-scroller': {
      overflow: 'auto',
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
      '-ms-overflow-style': 'none',
    },
    '& .MuiTabs-flexContainer': {
      gap: theme.spacing(0.5),
    },
  },
}));

export const StyledTab = styled(Tab)(({ theme }) => ({
  fontFamily: 'Roboto, sans-serif',
  fontSize: '14px',
  fontWeight: theme.typography.fontWeightMedium || 500,
  lineHeight: '24px',
  letterSpacing: '0.4px',
  textTransform: 'uppercase',
  padding: theme.spacing(1.125, 2), // 9px vertical, 16px horizontal
  color: theme.palette.text.secondary,
  minHeight: 'auto',
  '&.Mui-selected': {
    color: theme.palette.primary.main,
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px',
    lineHeight: '16px',
    letterSpacing: '0.2px',
    padding: theme.spacing(1, 1.5),
    minHeight: '44px',
  },
}));

// ============= Header Styled Components =============

/**
 * Shared header styled components for CaseDetailsHeader and OpinionDetailsHeader
 */
export const HeaderCard = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(3),
  backgroundColor: theme.palette.common.white,
  borderRadius: '20px',
  border: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    borderRadius: '12px',
  },
}));

export const HeaderTopSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(2),
  },
}));

export const HeaderTitleRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  gap: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
}));

export const TitleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.25),
  flex: 1,
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1),
    width: '100%',
  },
}));

export const CaseTitle = styled(Text)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: '30px',
  fontWeight: 700,
  lineHeight: '36px',
  color: theme.palette.text.primary,
  [theme.breakpoints.down('sm')]: {
    fontSize: '24px',
    lineHeight: '28px',
  },
}));

export const OpinionTitle = styled(Text)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: '30px',
  fontWeight: 700,
  lineHeight: '36px',
  color: theme.palette.text.primary,
  [theme.breakpoints.down('sm')]: {
    fontSize: '24px',
    lineHeight: '28px',
  },
}));

export const ExternalLinkIcon = styled(IconWrapper)(({ theme }) => ({
  color: theme.palette.primary.main,
  cursor: 'pointer',
  '& svg': {
    color: theme.palette.primary.main,
  },
  [theme.breakpoints.down('sm')]: {
    flexShrink: 0,
  },
}));
ExternalLinkIcon.defaultProps = { size: 24 };

export const LastUpdatedContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    alignSelf: 'flex-start',
  },
}));

export const LastUpdatedIcon = styled(IconWrapper)(({ theme }) => ({
  cursor: 'default',
  [theme.breakpoints.down('sm')]: {
    flexShrink: 0,
  },
}));
LastUpdatedIcon.defaultProps = { size: 20 };

export const LastUpdatedText = styled(Text)(({ theme }) => ({
  ...getTableCellSecondaryStyles(theme),
}));

export const CourtNameLink = styled(Text)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '24px',
  color: theme.palette.text.primary,
  textDecoration: 'underline',
  textDecorationSkipInk: 'none',
  cursor: 'pointer',
  '&:hover': {
    color: theme.palette.primary.main,
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
    lineHeight: '20px',
    wordBreak: 'break-word',
  },
}));

export const DetailsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: theme.spacing(1.125),
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: theme.spacing(1),
  },
}));

export const DetailPill = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  padding: theme.spacing(0.75, 1.5),
  borderRadius: '1000px',
  border: `1px solid ${theme.palette.divider}`,
  minHeight: '36px',
  gap: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.75, 1),
    gap: theme.spacing(0.75),
    minHeight: 'auto',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
  },
}));

export const DetailPillContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexShrink: 0,
  minWidth: 0,
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(0.75),
  },
}));

export const DetailIconContainer = styled(IconCircle)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    flexShrink: 0,
  },
}));

export const HeaderDetailLabel = styled(Text)(({ theme }) => ({
  ...getTableCellPrimaryStyles(theme),
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
    lineHeight: '16px',
  },
}));

export const HeaderDetailValue = styled(Text)(({ theme }) => ({
  ...getTableCellSecondaryStyles(theme),
  textAlign: 'right',
  flex: 1,
  minWidth: 0,
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  lineHeight: '16px',
  whiteSpace: 'normal',
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
    lineHeight: '16px',
    textAlign: 'right',
    wordBreak: 'break-word',
    flex: '1 1 auto',
  },
}));

// ============= Opinion Content Styled Components =============

/**
 * Shared styled components for opinion content display
 */
export const OpinionContentContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.common.white,
  borderRadius: theme.spacing(2.5),
  border: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1.5),
  },
}));

export const OpinionContentHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(2),
  gap: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
}));

export const OpinionContentTitle = styled(Text)(({ theme }) => ({
  fontSize: '18px',
  fontWeight: 600,
  color: theme.palette.text.primary,
  lineHeight: '24px',
}));

export const OpinionContentHtml = styled(Box)(({ theme }) => ({
  maxHeight: '500px',
  overflowY: 'auto',
  backgroundColor: alpha(theme.palette.grey[100], 0.5),
  padding: theme.spacing(2.5),
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
  fontSize: '0.875rem',
  lineHeight: 1.6,
  fontFamily: 'serif',
  color: theme.palette.text.primary,
  textAlign: 'center',
  '& a': {
    color: theme.palette.primary.main,
    textDecoration: 'underline',
  },
  '& p, & div, & span': {
    textAlign: 'center',
  },
}));

export const StyledActionButton = styled(Button)(({ theme }) => ({
  borderRadius: '32px',
  border: `1px solid ${theme.palette.primary.main}`,
  backgroundColor: 'transparent',
  color: theme.palette.primary.main,
  fontFamily: theme.typography.fontFamily,
  fontSize: '13px',
  fontWeight: 700,
  lineHeight: '22px',
  letterSpacing: '0.46px',
  textTransform: 'capitalize',
  padding: '4px 10px',
  minWidth: 'auto',
  flexShrink: 0,
  gap: theme.spacing(0.75),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    borderColor: alpha(theme.palette.primary.main, 0.5),
  },
  '& svg': {
    fontSize: '16px',
  },
}));

export const OpinionSummaryContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: alpha(theme.palette.primary.main, 0.05),
  borderRadius: theme.spacing(2),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  marginBottom: theme.spacing(3),
}));

export const OpinionSummaryTitle = styled(Text)(({ theme }) => ({
  fontSize: '20px',
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

export const OpinionSummaryContent = styled(Text)(({ theme }) => ({
  fontSize: '16px',
  lineHeight: 1.6,
  color: theme.palette.text.primary,
}));

export const DisclaimerContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: alpha(theme.palette.warning.main, 0.1),
  borderRadius: theme.spacing(1),
  border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
}));

export const OpinionDisclaimerText = styled(Text)(({ theme }) => ({
  fontSize: '14px',
  lineHeight: 1.5,
  color: theme.palette.text.secondary,
}));

export const InfoDisclaimerBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: alpha(theme.palette.info.main, 0.1),
  borderRadius: theme.spacing(1.5),
  marginBottom: theme.spacing(3),
  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
}));

export const OpinionSummaryLoadingOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: alpha(theme.palette.common.white, 0.9),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
  borderRadius: theme.spacing(2.5),
}));

export const opinionSummaryLoadingCardStyles = {
  padding: 4,
  backgroundColor: 'common.white',
  borderRadius: 3,
  boxShadow: 3,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 2,
};

export const opinionSummaryLoadingIconStyles = {
  fontSize: 48,
  color: 'primary.main',
};

export const opinionSummaryLoadingTitleStyles = {
  fontWeight: 600,
  color: 'text.primary',
};

export const opinionSummaryLoadingTextStyles = {
  color: 'text.secondary',
  textAlign: 'center',
};

// ============= Opinion List Styled Components =============

export const OpinionListContainer = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

export const OpinionListEmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(6),
  textAlign: 'center',
}));

export const OpinionListDisclaimer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
  alignItems: 'flex-start',
  marginBottom: theme.spacing(3),
}));

export const OpinionListDisclaimerText = styled(Text)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '24px',
  color: theme.palette.text.primary,
  letterSpacing: 0,
  marginTop: theme.spacing(0.25),
}));

export const OpinionListContent = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(3),
}));
